// Pure, deterministic Othello (Reversi) engine. No I/O, no wall-clock in game-critical
// fields. Standard 8×8 rules; dark moves first. See docs/OTHELLO_GAME_SPEC.md §4–5.

import type { PlayerId } from "../game-core";

export type Disc = "dark" | "light";

export const SIZE = 8;
export const CELLS = SIZE * SIZE;

const EMPTY = ".";
const DARK = "D";
const LIGHT = "L";

/** Eight ray directions as [rowDelta, colDelta]. Order is fixed for determinism. */
const DIRECTIONS: ReadonlyArray<readonly [number, number]> = [
  [-1, -1], [-1, 0], [-1, 1],
  [0, -1], /*  */ [0, 1],
  [1, -1], [1, 0], [1, 1],
];

export type OthelloMove =
  | { version: 1; type: "place"; cell: number }
  | { version: 1; type: "pass" }
  | { version: 1; type: "resign" };

export type OthelloOutcome = "dark_win" | "light_win" | "draw" | "resigned";

export type OthelloLastMove =
  | null
  | { playerId: PlayerId; type: "place"; cell: number; flipped: number[] }
  | { playerId: PlayerId; type: "pass" }
  | { playerId: PlayerId; type: "resign" };

export interface OthelloState {
  version: 1;
  rulesVersion: "othello/1";
  match: {
    phase: "playing" | "complete" | "resigned";
    colors: Record<PlayerId, Disc>;
    activePlayerId: PlayerId;
    winnerId: PlayerId | null;
    outcome: OthelloOutcome | null;
  };
  players: [PlayerId, PlayerId]; // [dark, light]
  board: string; // CELLS chars over {'.','D','L'}, index = row*SIZE + col
  discCounts: { dark: number; light: number };
  moveNumber: number;
  passStreak: 0 | 1 | 2;
  lastMove: OthelloLastMove;
  revision: number;
  lastActionAt: string;
}

const discChar = (disc: Disc): string => (disc === "dark" ? DARK : LIGHT);
const other = (disc: Disc): Disc => (disc === "dark" ? "light" : "dark");

export const rowOf = (cell: number): number => Math.floor(cell / SIZE);
export const colOf = (cell: number): number => cell % SIZE;
export const cellOf = (row: number, col: number): number => row * SIZE + col;

/** Cells (0-based) flipped if `disc` is placed on empty `cell`; empty when the move is illegal. */
export function flipsFor(board: string, cell: number, disc: Disc): number[] {
  if (board[cell] !== EMPTY) return [];
  const mine = discChar(disc);
  const theirs = discChar(other(disc));
  const flipped: number[] = [];

  for (const [dr, dc] of DIRECTIONS) {
    const run: number[] = [];
    let r = rowOf(cell) + dr;
    let c = colOf(cell) + dc;
    while (r >= 0 && r < SIZE && c >= 0 && c < SIZE) {
      const at = cellOf(r, c);
      const ch = board[at];
      if (ch === theirs) {
        run.push(at);
      } else if (ch === mine) {
        // Bracketed a non-empty run of opponent discs — this direction contributes.
        if (run.length > 0) flipped.push(...run);
        break;
      } else {
        break; // empty cell or edge: no bracket in this direction.
      }
      r += dr;
      c += dc;
    }
  }
  return flipped;
}

/** All cells where `disc` has at least one legal placement. */
export function legalCells(board: string, disc: Disc): number[] {
  const cells: number[] = [];
  for (let cell = 0; cell < CELLS; cell++) {
    if (board[cell] === EMPTY && flipsFor(board, cell, disc).length > 0) cells.push(cell);
  }
  return cells;
}

const countDiscs = (board: string): { dark: number; light: number } => {
  let dark = 0;
  let light = 0;
  for (const ch of board) {
    if (ch === DARK) dark++;
    else if (ch === LIGHT) light++;
  }
  return { dark, light };
};

const withChar = (board: string, cell: number, ch: string): string =>
  board.slice(0, cell) + ch + board.slice(cell + 1);

/** Standard opening position: two dark and two light discs on the central four squares. */
export function createInitialState(
  players: [PlayerId, PlayerId],
  at: string = new Date().toISOString(),
): OthelloState {
  const [dark, light] = players;
  if (dark === light) throw new Error("othello: players must be distinct");
  let board = EMPTY.repeat(CELLS);
  board = withChar(board, cellOf(3, 3), LIGHT);
  board = withChar(board, cellOf(3, 4), DARK);
  board = withChar(board, cellOf(4, 3), DARK);
  board = withChar(board, cellOf(4, 4), LIGHT);
  return {
    version: 1,
    rulesVersion: "othello/1",
    match: {
      phase: "playing",
      colors: { [dark]: "dark", [light]: "light" },
      activePlayerId: dark,
      winnerId: null,
      outcome: null,
    },
    players: [dark, light],
    board,
    discCounts: countDiscs(board),
    moveNumber: 0,
    passStreak: 0,
    lastMove: null,
    revision: 0,
    lastActionAt: at,
  };
}

const colorOf = (state: OthelloState, playerId: PlayerId): Disc | undefined =>
  state.match.colors[playerId];

const opponentOf = (state: OthelloState, playerId: PlayerId): PlayerId =>
  state.players[0] === playerId ? state.players[1] : state.players[0];

/** Whether `playerId` must pass: it is their turn, the game is live, and they have no placement. */
export function mustPass(state: OthelloState, playerId: PlayerId): boolean {
  if (state.match.phase !== "playing") return false;
  if (state.match.activePlayerId !== playerId) return false;
  const disc = colorOf(state, playerId);
  if (!disc) return false;
  return legalCells(state.board, disc).length === 0;
}

/** Legal moves for a player: placements, or a single forced pass, or nothing off-turn/finished. */
export function legalMoves(state: OthelloState, playerId: PlayerId): OthelloMove[] {
  if (state.match.phase !== "playing" || state.match.activePlayerId !== playerId) return [];
  const disc = colorOf(state, playerId);
  if (!disc) return [];
  const cells = legalCells(state.board, disc);
  if (cells.length === 0) return [{ version: 1, type: "pass" }];
  return cells.map((cell) => ({ version: 1, type: "place", cell }));
}

export function validateMove(state: OthelloState, move: OthelloMove, playerId: PlayerId): void {
  if (state.match.phase !== "playing") throw new Error("othello: game is not in progress");
  if (state.match.activePlayerId !== playerId) throw new Error("othello: not your turn");
  const disc = colorOf(state, playerId);
  if (!disc) throw new Error("othello: player is not in this match");

  if (move.type === "resign") return;

  if (move.type === "pass") {
    if (legalCells(state.board, disc).length > 0) throw new Error("othello: cannot pass with a legal move available");
    return;
  }

  if (move.type === "place") {
    if (!Number.isInteger(move.cell) || move.cell < 0 || move.cell >= CELLS) throw new Error("othello: cell out of range");
    if (state.board[move.cell] !== EMPTY) throw new Error("othello: cell is occupied");
    if (flipsFor(state.board, move.cell, disc).length === 0) throw new Error("othello: move flips no discs");
    return;
  }

  throw new Error("othello: unknown move type");
}

const finalize = (state: OthelloState, outcome: OthelloOutcome, winnerId: PlayerId | null): OthelloState["match"] => ({
  ...state.match,
  phase: outcome === "resigned" ? "resigned" : "complete",
  outcome,
  winnerId,
});

/** Winner from disc counts (draw when equal). */
export function scoreOutcome(state: OthelloState): { outcome: OthelloOutcome; winnerId: PlayerId | null } {
  const { dark, light } = state.discCounts;
  if (dark === light) return { outcome: "draw", winnerId: null };
  const winnerColor: Disc = dark > light ? "dark" : "light";
  const winnerId = state.players[winnerColor === "dark" ? 0 : 1];
  return { outcome: winnerColor === "dark" ? "dark_win" : "light_win", winnerId };
}

/** Apply a validated move. Board/counts/turn are deterministic; `at` is informational only. */
export function reduce(state: OthelloState, move: OthelloMove, at: string = new Date().toISOString()): OthelloState {
  validateMove(state, move, state.match.activePlayerId);
  const playerId = state.match.activePlayerId;
  const opponent = opponentOf(state, playerId);
  const base = { ...state, moveNumber: state.moveNumber + 1, revision: state.revision + 1, lastActionAt: at };

  if (move.type === "resign") {
    const winnerId = opponent;
    return {
      ...base,
      match: finalize(state, "resigned", winnerId),
      lastMove: { playerId, type: "resign" },
    };
  }

  if (move.type === "pass") {
    const passStreak = (state.passStreak + 1) as 0 | 1 | 2;
    const next: OthelloState = {
      ...base,
      match: { ...state.match, activePlayerId: opponent },
      passStreak,
      lastMove: { playerId, type: "pass" },
    };
    if (passStreak >= 2) {
      const { outcome, winnerId } = scoreOutcome(next);
      return { ...next, match: finalize(next, outcome, winnerId) };
    }
    return next;
  }

  // place
  const disc = state.match.colors[playerId];
  const flipped = flipsFor(state.board, move.cell, disc);
  let board = withChar(state.board, move.cell, discChar(disc));
  for (const at2 of flipped) board = withChar(board, at2, discChar(disc));
  const discCounts = countDiscs(board);

  const next: OthelloState = {
    ...base,
    board,
    discCounts,
    passStreak: 0,
    match: { ...state.match, activePlayerId: opponent },
    lastMove: { playerId, type: "place", cell: move.cell, flipped },
  };

  // A full board ends the game immediately; otherwise play continues (a stuck player passes).
  if (discCounts.dark + discCounts.light === CELLS) {
    const { outcome, winnerId } = scoreOutcome(next);
    return { ...next, match: finalize(next, outcome, winnerId) };
  }
  return next;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

/**
 * Validate untrusted JSON into an OthelloState. Recomputes disc counts from the board and
 * cross-checks them, so a peer cannot lie about the score without an inconsistent board.
 * Throws on any violation. See docs/OTHELLO_GAME_SPEC.md §5.3.
 */
export function validateState(snapshot: unknown): OthelloState {
  if (!isRecord(snapshot)) throw new Error("othello: state is not an object");
  const s = snapshot as Record<string, unknown>;
  if (s.version !== 1) throw new Error("othello: unsupported state version");
  if (s.rulesVersion !== "othello/1") throw new Error("othello: unsupported rules version");

  const board = s.board;
  if (typeof board !== "string" || board.length !== CELLS || /[^.DL]/.test(board)) {
    throw new Error("othello: invalid board");
  }

  const players = s.players;
  if (!Array.isArray(players) || players.length !== 2 || typeof players[0] !== "string" || typeof players[1] !== "string" || players[0] === players[1]) {
    throw new Error("othello: invalid players");
  }
  const [dark, light] = players as [string, string];

  const match = s.match;
  if (!isRecord(match)) throw new Error("othello: invalid match");
  if (match.phase !== "playing" && match.phase !== "complete" && match.phase !== "resigned") throw new Error("othello: invalid phase");
  const colors = match.colors;
  if (!isRecord(colors) || colors[dark] !== "dark" || colors[light] !== "light" || Object.keys(colors).length !== 2) {
    throw new Error("othello: invalid colors");
  }
  if (match.activePlayerId !== dark && match.activePlayerId !== light) throw new Error("othello: invalid active player");
  if (match.winnerId !== null && match.winnerId !== dark && match.winnerId !== light) throw new Error("othello: invalid winner");

  const counts = countDiscs(board);
  const declared = s.discCounts;
  if (!isRecord(declared) || declared.dark !== counts.dark || declared.light !== counts.light) {
    throw new Error("othello: disc counts do not match board");
  }

  if (typeof s.moveNumber !== "number" || typeof s.revision !== "number") throw new Error("othello: invalid counters");
  if (s.passStreak !== 0 && s.passStreak !== 1 && s.passStreak !== 2) throw new Error("othello: invalid passStreak");
  if (typeof s.lastActionAt !== "string") throw new Error("othello: invalid lastActionAt");

  // Structure is sound; the object is one we produced or an equivalent. Trust the typed shape.
  return snapshot as unknown as OthelloState;
}

export const isTerminal = (state: OthelloState): boolean =>
  state.match.phase === "complete" || state.match.phase === "resigned";

export const activePlayer = (state: OthelloState): PlayerId | null =>
  state.match.phase === "playing" ? state.match.activePlayerId : null;
